/**
 * Three-state theme choice: light · dark · system.
 *
 * `system` is represented by the ABSENCE of `data-theme` on <html>, not by the
 * string "system" written into the attribute. That is what lets the stylesheet's
 * `@media (prefers-color-scheme: light)` block take effect at all — an attribute
 * of any value would win over it and the OS preference would be ignored.
 */
import { withThemeTransitionSuppressed } from './theme-transition.ts';


export const THEME_CHOICES = ['light', 'dark', 'system'] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];

export const DEFAULT_THEME: ThemeChoice = 'system';

export function isThemeChoice(v: unknown): v is ThemeChoice {
  return typeof v === 'string' && (THEME_CHOICES as readonly string[]).includes(v);
}

/**
 * Apply a choice to a document root. Safe to call before React mounts.
 *
 * The write is bracketed by `withThemeTransitionSuppressed` because light and
 * dark are an inversion, and interpolating an inversion puts the text through
 * the colour of its own background — 1.02:1, for up to 180ms. The reason is
 * written out in full in ./theme-transition.ts.
 *
 * It is done HERE rather than in the toggle so that every caller gets it:
 * the toggle, the player portal's provider, and anything added later that only
 * knows it wants to set a theme.
 */
export function applyThemeChoice(choice: ThemeChoice, root: HTMLElement): void {
  withThemeTransitionSuppressed(root, () => {
    if (choice === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', choice);
  });
}

/**
 * Read the stored choice. Storage can throw outright — a private window, a
 * browser set to block site data, a thumbnail renderer — so every access is
 * guarded and an unreadable store simply means "system".
 */
export function readStoredChoice(storageKey: string): ThemeChoice {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey);
    return isThemeChoice(raw) ? raw : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function writeStoredChoice(storageKey: string, choice: ThemeChoice): void {
  try {
    globalThis.localStorage?.setItem(storageKey, choice);
  } catch {
    /* A theme preference is not worth breaking the page over. */
  }
}

/**
 * The pre-paint script, as a string to inline in <head>.
 *
 * It must run BEFORE first paint or the page flashes the wrong theme, which
 * rules out doing this in a React effect. It is deliberately tiny and total:
 * any throw leaves the attribute unset, which is `system`, which is correct.
 *
 * `themeInitScript` and `applyThemeChoice` must stay in step — there is a test
 * that evaluates this string and asserts it produces the same attribute as the
 * function for all three choices.
 */
export function themeInitScript(storageKey: string): string {
  return `(function(){try{var c=localStorage.getItem(${JSON.stringify(storageKey)});if(c==="light"||c==="dark"){document.documentElement.setAttribute("data-theme",c)}else{document.documentElement.removeAttribute("data-theme")}}catch(e){}})()`;
}

/** What the user actually sees right now, resolving `system` against the OS. */
export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  try {
    return globalThis.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}
